export interface SatCatalogItem {
  code: string;
  name: string;
}

export const REGIMENES_FISCALES: SatCatalogItem[] = [
  { code: '601', name: '601 - General de Ley Personas Morales' },
  { code: '603', name: '603 - Personas Morales con Fines no Lucrativos' },
  { code: '605', name: '605 - Sueldos y Salarios e Ingresos Asimilados a Salarios' },
  { code: '606', name: '606 - Arrendamiento' },
  { code: '607', name: '607 - Régimen de Enajenación o Adquisición de Bienes' },
  { code: '608', name: '608 - Demás ingresos' },
  { code: '610', name: '610 - Residentes en el Extranjero sin Establecimiento Permanente' },
  { code: '611', name: '611 - Ingresos por Dividendos (socios y accionistas)' },
  { code: '612', name: '612 - Personas Físicas con Actividades Empresariales y Profesionales' },
  { code: '614', name: '614 - Ingresos por intereses' },
  { code: '615', name: '615 - Régimen de los ingresos por obtención de premios' },
  { code: '616', name: '616 - Sin obligaciones fiscales' },
  { code: '620', name: '620 - Sociedades Cooperativas de Producción que difieren ingresos' },
  { code: '621', name: '621 - Incorporación Fiscal' },
  { code: '622', name: '622 - Actividades Agrícolas, Ganaderas, Silvícolas y Pesqueras' },
  { code: '623', name: '623 - Opcional para Grupos de Sociedades' },
  { code: '624', name: '624 - Coordinados' },
  { code: '625', name: '625 - Actividades Empresariales con ingresos a través de Plataformas Tecnológicas' },
  { code: '626', name: '626 - Régimen Simplificado de Confianza - RESICO' },
];

/** Full régimen label (code + name), e.g. "626 - Régimen Simplificado de Confianza - RESICO". */
export const getRegimenLabel = (code?: string | null): string => {
  if (!code) return '';
  return (
    REGIMENES_FISCALES.find((r) => r.code === code)?.name ?? `Régimen ${code}`
  );
};

/**
 * Regímenes que el SAT reserva a un solo tipo de persona (c_RegimenFiscal, columnas Física
 * y Moral). El RFC decide cuál es: 12 caracteres, moral; 13, física.
 */
const MORAL_ONLY_REGIMES = new Set(['601', '603', '620', '622', '623', '624']);
const FISICA_ONLY_REGIMES = new Set([
  '605', '606', '607', '608', '611', '612', '614', '615', '616', '621', '625',
]);

export const regimeFitsRfc = (regimeCode: string, rfc: string): boolean =>
  rfc.trim().length === 12
    ? !FISICA_ONLY_REGIMES.has(regimeCode)
    : !MORAL_ONLY_REGIMES.has(regimeCode);

/** Priority orderings for the public QR request page dropdowns (most common first). */
export const orderByPriority = (
  items: SatCatalogItem[],
  priorityCodes: string[],
): SatCatalogItem[] => {
  const rank = new Map(priorityCodes.map((code, i) => [code, i]));
  return [...items].sort((a, b) => {
    const ra = rank.has(a.code) ? (rank.get(a.code) as number) : Infinity;
    const rb = rank.has(b.code) ? (rank.get(b.code) as number) : Infinity;
    return ra - rb;
  });
};

export const USOS_CFDI: SatCatalogItem[] = [
  { code: 'G01', name: 'G01 - Adquisición de mercancías' },
  { code: 'G02', name: 'G02 - Devoluciones, descuentos o bonificaciones' },
  { code: 'G03', name: 'G03 - Gastos en general' },
  { code: 'I01', name: 'I01 - Construcciones' },
  { code: 'I02', name: 'I02 - Mobiliario y equipo de oficina por inversiones' },
  { code: 'I03', name: 'I03 - Equipo de transporte' },
  { code: 'I04', name: 'I04 - Equipo de cómputo y accesorios' },
  { code: 'I05', name: 'I05 - Dados, troqueles, moldes, matrices y herramental' },
  { code: 'I06', name: 'I06 - Comunicaciones telefónicas' },
  { code: 'I07', name: 'I07 - Comunicaciones satelitales' },
  { code: 'I08', name: 'I08 - Otra maquinaria y equipo' },
  { code: 'D01', name: 'D01 - Honorarios médicos, dentales y gastos hospitalarios' },
  { code: 'D02', name: 'D02 - Gastos médicos por incapacidad o discapacidad' },
  { code: 'D03', name: 'D03 - Gastos funerales' },
  { code: 'D04', name: 'D04 - Donativos' },
  { code: 'D05', name: 'D05 - Intereses reales por créditos hipotecarios (casa habitación)' },
  { code: 'D06', name: 'D06 - Aportaciones voluntarias al SAR' },
  { code: 'D07', name: 'D07 - Primas por seguros de gastos médicos' },
  { code: 'D08', name: 'D08 - Gastos de transportación escolar obligatoria' },
  { code: 'D09', name: 'D09 - Depósitos en cuentas para el ahorro / pensiones' },
  { code: 'D10', name: 'D10 - Pagos por servicios educativos (colegiaturas)' },
  { code: 'S01', name: 'S01 - Sin efectos fiscales' },
  { code: 'CP01', name: 'CP01 - Pagos' },
  { code: 'CN01', name: 'CN01 - Nómina' },
];

/**
 * UsoCFDI ↔ régimen fiscal compatibility (SAT c_UsoCFDI, CFDI 4.0).
 *
 * SOURCE OF TRUTH: heru-core-invoice-service/src/modules/invoices/constants/uso-cfdi.constants.ts
 * The backend rejects an incompatible uso at stamp time; mirroring the rule here just stops the
 * QR client from picking a combination that would dead-end downstream (the owner can't edit these
 * fields when reviewing the request). Keep both files in sync if the SAT catalog changes.
 *
 * Person type (física/moral) is intentionally NOT modeled: the régimen already implies it in
 * practice (a moral taxpayer only picks 601/603/620/623, which allow únicamente usos "ambos").
 */
const ACTIVIDAD_EMPRESARIAL_REGIMES = [
  '601', '603', '606', '612', '620', '621', '622', '623', '624', '625', '626',
];
const DEDUCCION_PERSONAL_REGIMES = [
  '605', '606', '607', '608', '611', '612', '614', '615', '625',
];
// S01 y CP01 son universales — todos los regímenes los permiten.
const ALL_REGIMES = [
  '601', '603', '605', '606', '607', '608', '610', '611',
  '612', '614', '615', '616', '620', '621', '622', '623',
  '624', '625', '626',
];

/** For each UsoCFDI, the regímenes fiscales del receptor for which the SAT accepts it. */
const USO_CFDI_VALID_REGIMES: Record<string, string[]> = {
  G01: ACTIVIDAD_EMPRESARIAL_REGIMES,
  G02: ACTIVIDAD_EMPRESARIAL_REGIMES,
  G03: ACTIVIDAD_EMPRESARIAL_REGIMES,
  I01: ACTIVIDAD_EMPRESARIAL_REGIMES,
  I02: ACTIVIDAD_EMPRESARIAL_REGIMES,
  I03: ACTIVIDAD_EMPRESARIAL_REGIMES,
  I04: ACTIVIDAD_EMPRESARIAL_REGIMES,
  I05: ACTIVIDAD_EMPRESARIAL_REGIMES,
  I06: ACTIVIDAD_EMPRESARIAL_REGIMES,
  I07: ACTIVIDAD_EMPRESARIAL_REGIMES,
  I08: ACTIVIDAD_EMPRESARIAL_REGIMES,
  D01: DEDUCCION_PERSONAL_REGIMES,
  D02: DEDUCCION_PERSONAL_REGIMES,
  D03: DEDUCCION_PERSONAL_REGIMES,
  D04: DEDUCCION_PERSONAL_REGIMES,
  D05: DEDUCCION_PERSONAL_REGIMES,
  D06: DEDUCCION_PERSONAL_REGIMES,
  D07: DEDUCCION_PERSONAL_REGIMES,
  D08: DEDUCCION_PERSONAL_REGIMES,
  D09: DEDUCCION_PERSONAL_REGIMES,
  D10: DEDUCCION_PERSONAL_REGIMES,
  S01: ALL_REGIMES,
  CP01: ALL_REGIMES,
  CN01: ['605'],
};

/**
 * Usos de CFDI válidos para un régimen fiscal dado. Returns the full USOS_CFDI list when the
 * régimen is empty/unknown, so the dropdown never ends up blank before a régimen is chosen.
 */
export const allowedUsosForRegime = (regime?: string | null): SatCatalogItem[] => {
  if (!regime) return USOS_CFDI;
  return USOS_CFDI.filter((u) => USO_CFDI_VALID_REGIMES[u.code]?.includes(regime));
};

/**
 * Default UsoCFDI for a régimen. Mirrors the backend helper, but the caller must still verify the
 * result against `allowedUsosForRegime` — the backend default (G03) is NOT valid for every régimen
 * (e.g. 607), so it's a hint, not a guarantee.
 */
export const getDefaultUsoCfdiForRegime = (regime: string): string => {
  const defaults: Record<string, string> = {
    '605': 'CP01',
    '612': 'G03',
    '625': 'G03',
    '626': 'G03',
  };
  return defaults[regime] || 'G03';
};

/**
 * FormaPago (c_FormaPago) — how the client paid. Set by the owner at accept/stamp time
 * for the QR flow (the invoice is always PUE, so "99 Por definir" is intentionally omitted).
 */
export const FORMAS_PAGO: SatCatalogItem[] = [
  { code: '01', name: '01 - Efectivo' },
  { code: '02', name: '02 - Cheque nominativo' },
  { code: '03', name: '03 - Transferencia electrónica de fondos' },
  { code: '04', name: '04 - Tarjeta de crédito' },
  { code: '28', name: '28 - Tarjeta de débito' },
];

/** Default FormaPago pre-selected when the owner accepts a request. */
export const DEFAULT_FORMA_PAGO = '03';

/**
 * Full c_FormaPago catalog, ordered by how often it is actually used. "99 Por
 * definir" belongs here because a PPD invoice is required to carry it.
 */
export const FORMAS_PAGO_COMPLETAS: SatCatalogItem[] = [
  { code: '03', name: '03 - Transferencia electrónica de fondos' },
  { code: '01', name: '01 - Efectivo' },
  { code: '04', name: '04 - Tarjeta de crédito' },
  { code: '28', name: '28 - Tarjeta de débito' },
  { code: '02', name: '02 - Cheque nominativo' },
  { code: '99', name: '99 - Por definir' },
  { code: '05', name: '05 - Monedero electrónico' },
  { code: '06', name: '06 - Dinero electrónico' },
  { code: '08', name: '08 - Vales de despensa' },
  { code: '29', name: '29 - Tarjeta de servicios' },
  { code: '30', name: '30 - Aplicación de anticipos' },
  { code: '31', name: '31 - Intermediario pagos' },
  { code: '12', name: '12 - Dación en pago' },
  { code: '13', name: '13 - Pago por subrogación' },
  { code: '14', name: '14 - Pago por consignación' },
  { code: '15', name: '15 - Condonación' },
  { code: '17', name: '17 - Compensación' },
  { code: '23', name: '23 - Novación' },
  { code: '24', name: '24 - Confusión' },
  { code: '25', name: '25 - Remisión de deuda' },
  { code: '26', name: '26 - Prescripción o caducidad' },
  { code: '27', name: '27 - A satisfacción del acreedor' },
];

/**
 * FormaDePagoP of a payment complement: the same catalog without "99 Por
 * definir". A complement documents a payment that already happened, so the SAT
 * rejects the code that means "not defined yet".
 */
export const FORMAS_PAGO_COMPLEMENTO: SatCatalogItem[] = FORMAS_PAGO_COMPLETAS.filter(
  (forma) => forma.code !== '99',
);
